<template>
  <ContainerWithErrorMessage :error-message="errorMessage" class="w-100">
    <v-btn-group class="my-4">
      <v-text-field
        :label="prettyName"
        :prepend-inner-icon="props.field.properties.icon"
        disabled
        :placeholder="$t(props.field.properties.placeholder)"
        :model-value="relatedObjectProperty"
        width="500px"
      />
      <v-btn
        v-show="isEditable"
        class="me-4"
        color="primary"
        @click="openListSelection"
      >
        {{ $t('components.common.selection') }}
      </v-btn>
    </v-btn-group>
    <div v-show="showList">
      <BaseListV2
        v-show="showList"
        :model-class="modelClass"
        :can-add="false"
        :can-select="true"
        :can-delete="false"
        :single-selection="true"
        :has-detail-view="false"
        :collection-filter="collectionFilter"
        :show-filters="showFilter"
        @row-selection="onSelect"
      />
    </div>
  </ContainerWithErrorMessage>
</template>

<script setup lang="ts">
import { useFormKitContext } from '@formkit/vue'
import { computed, onMounted, ref } from 'vue'
import { useRoute } from 'vue-router'
import { useStore } from 'vuex'

import { apiClientV2 } from '@/api/ApiClientV2'
import errorHandler from '@/api/ErrorHandler'
import BaseListV2 from '@/components/common/BaseListV2.vue'
import ContainerWithErrorMessage from '@/components/common/ContainerWithErrorMessage.vue'
import { TransientBaseObjectType } from '@/models/core/base'
import { getModelClass } from '@/models/objectRegistry'

import type { SchemaFormKitProps } from './types'
const route = useRoute()
const store = useStore()

const context = useFormKitContext()
const props = defineProps<SchemaFormKitProps>()

const modelClass: TransientBaseObjectType = getModelClass(
  props.field?.properties?.modelClass
)
const showList = ref(false)
const relatedObjectProperty: any = ref(null)
const selection: any = ref(null)
const originalRelatedModel: any = ref(null)
const loading = ref(false)
const errorMessage = ref('')

onMounted(async () => {
  let id
  if (context.value.value.value[props.field.key]) {
    id = context.value.value.value[props.field.key]
  } else if (route.query[props.field.key]) {
    id = route.query[props.field.key]
    context.value.value.value[props.field.key] = id
  }

  if (id) {
    loading.value = true
    try {
      originalRelatedModel.value = await apiClientV2.get(modelClass, id)
      relatedObjectProperty.value =
        originalRelatedModel.value[
          props.field?.properties?.relatedObjectProperty
        ]
      loading.value = false
    } catch (err) {
      // TODO: Error msg!
      loading.value = false
      errorMessage.value = errorHandler.errorToString(err)
    }
  }
})

function onSelect(newSelection) {
  showList.value = false
  if (newSelection.length > 0) {
    selection.value = newSelection[0]
    context.value.value.value[props.field.key] = selection.value.id
    relatedObjectProperty.value = null
    if (props.field?.properties?.relatedObjectProperty) {
      relatedObjectProperty.value =
        newSelection[0][props.field?.properties?.relatedObjectProperty]
    }
  } else {
    selection.value = null
  }
}

function openListSelection() {
  showList.value = !showList.value
}

const collectionFilter = computed(() => {
  let collectionFilter = {}
  if (props.field.properties.filter) {
    for (const filter of props.field.properties.filter) {
      if (filter === 'organisation') {
        collectionFilter['organisation'] =
          store.getters['global/organisation'].id
      } else {
        console.log(`Filter by collection ${filter} not supported yet.`)
      }
    }
  }
  return collectionFilter
})

const showFilter = computed(() => {
  if (props.field?.showFilter === undefined) return true

  return props.field?.showFilter
})

const isEditable = computed(() => {
  if (props.field.editable === undefined) {
    return true
  }
  if (props.field.editable === false && context.value.value.id !== '0') {
    return false
  }
  return true
})

const prettyName = computed((): string => {
  let prettyName = modelClass.prettyName()
  if (props.field && props.field.required) {
    // add star for required field
    prettyName += ' *'
  }

  return prettyName
})
</script>
